/**
 * ETMF Service Utilities
 * Migrated from legacy app/service/etmf.service.js
 */

import { instancedPropsSearch, getEnvironmentLevels } from './api/etmf'
import Constants from '../constants/constants'

/**
 * Sort items by fsiName property (case-insensitive alphabetical order).
 * Matches legacy etmfService.sortByFsiName.
 * @param items - Array of items with fsiName property
 * @returns Sorted array (new array, does not mutate input)
 */
export function sortByFsiName<T extends { fsiName?: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const aName = a.fsiName ? a.fsiName.toLowerCase() : ''
    const bName = b.fsiName ? b.fsiName.toLowerCase() : ''
    return aName.localeCompare(bName)
  })
}

/**
 * Get level options for dropdown in File to ETMF form.
 * Matches legacy etmfService.getLevelOptionsForDropdown.
 * Returns object with keys as level values and values as translated labels.
 *
 * NOTE: This returns raw translation keys. The component should use useTranslation() to translate.
 * @returns Object with level options
 */
export function getLevelOptionsForDropdown(): Record<string, string> {
  return {
    PRODUCT: 'Product',
    STUDY: 'Study',
    COUNTRY: 'Country',
    SITE: 'Site'
  }
}

/**
 * Get unique FSI names from array of items.
 * Matches legacy etmfService.getUniqueFsiNames.
 * @param items - Array of items with fsiName property
 * @returns Array of unique items (by fsiName)
 */
export function getUniqueFsiNames<T extends { fsiName?: string }>(items: T[]): T[] {
  const seen = new Set<string>()
  return items.filter((item) => {
    const name = item.fsiName || ''
    if (seen.has(name)) {
      return false
    }
    seen.add(name)
    return true
  })
}
/*
 * ETMF Service - Business Logic Layer
 *
 * This service provides business logic functions that wrap ETMF API calls
 * with filtering, sorting, and data transformation logic.
 *
 * Migrated from: app/service/etmf.service.js
 */

/**
 * Query parameter interface for ETMF structure queries
 */
export interface EtmfQueryParams {
  product?: string | { fsiName: string }
  trial?: string | { fsiName: string }
  environment?: string | { fsiName: string }
  country?: string | { fsiName: string }
}

/**
 * Metadata filter object structure
 */
export interface MetadataFilter {
  tplId: string
  vals?: string
}

/**
 * ETMF structure item with FSI information
 */
export interface EtmfStructureItem {
  fsiId: string
  fsiName: string
}

/**
 * Check if only one item has the given fsiName.
 * Matches legacy etmfService.isOnlyFsiName.
 * @param items - Array of items with fsiName property
 * @param name - The fsiName to check
 * @returns True if exactly one item has the given fsiName
 */
export function isOnlyFsiName<T extends { fsiName?: string }>(items: T[], name: string): boolean {
  const filteredItems = items.filter((item) => item.fsiName === name)
  return filteredItems.length === 1
}
/*
 * Assemble metadata filters from query parameters and environment levels
 *
 * Builds an array of metadata filter objects based on the hierarchy:
 * - If product is specified, add product filter
 * - If trial is specified, add trial filter
 * - If environment is specified, add environment filter
 * - If country is specified, add country filter
 * - If type is specified, add filter for that level (without vals)
 *
 * @param type - ETMF level type (PRODUCT, TRIAL, ENVIRONMENT, COUNTRY, SITE)
 * @param queryParams - Object containing hierarchy parameters
 * @returns Promise resolving to array of metadata filters
 */
export const assembleMetadataFilters = async (
  type?: string,
  queryParams?: EtmfQueryParams
): Promise<MetadataFilter[]> => {
  const params = queryParams || {}

  // Get environment level template IDs
  const environmentLevels = await getEnvironmentLevels(true)

  const filterParams: MetadataFilter[] = []

  // Helper function to extract fsiName from string or object
  const getFsiName = (instance: string | { fsiName: string } | undefined): string | undefined => {
    if (typeof instance === 'string') {
      return instance
    }
    if (instance && typeof instance === 'object' && 'fsiName' in instance) {
      return instance.fsiName
    }
    return undefined
  }

  // Build filter array based on query params
  if (params.product) {
    const vals = getFsiName(params.product)
    if (vals) {
      const productLevel = environmentLevels[Constants.etmf.masterData.type.PRODUCT as keyof typeof environmentLevels]
      if (productLevel) {
        filterParams.push({
          tplId: productLevel.id as string,
          vals
        })
      }
    }
  }

  if (params.trial) {
    const vals = getFsiName(params.trial)
    if (vals) {
      const trialLevel = environmentLevels[Constants.etmf.masterData.type.TRIAL as keyof typeof environmentLevels]
      if (trialLevel) {
        filterParams.push({
          tplId: trialLevel.id as string,
          vals
        })
      }
    }
  }

  if (params.environment) {
    const vals = getFsiName(params.environment)
    if (vals) {
      const envLevel = environmentLevels[Constants.etmf.masterData.type.ENVIRONMENT as keyof typeof environmentLevels]
      if (envLevel) {
        filterParams.push({
          tplId: envLevel.id as string,
          vals
        })
      }
    }
  }

  if (params.country) {
    const vals = getFsiName(params.country)
    if (vals) {
      const countryLevel = environmentLevels[Constants.etmf.masterData.type.COUNTRY as keyof typeof environmentLevels]
      if (countryLevel) {
        filterParams.push({
          tplId: countryLevel.id as string,
          vals
        })
      }
    }
  }

  // If querying for a specific level, add filter without vals to get all items at that level
  if (type) {
    const typeLevel = environmentLevels[type as keyof typeof environmentLevels]
    if (typeLevel) {
      filterParams.push({
        tplId: typeLevel.id as string
      })
    }
  }

  return filterParams
}

/**
 * Query ETMF structure at a specific level with filtering
 *
 * This method:
 * 1. Assembles metadata filters based on query parameters
 * 2. Calls instancedPropsSearch API to get items at the specified level
 * 3. Sorts results by fsiName
 *
 * Used for loading dropdown options in Update Study Dialog:
 * - loadProducts() - Query PRODUCT level
 * - loadStudyOptions() - Query TRIAL level with product filter
 * - loadEnvironmentOptions() - Query ENVIRONMENT level with product+trial filters
 *
 * @param type - ETMF level type (PRODUCT, TRIAL, ENVIRONMENT, COUNTRY, SITE)
 * @param queryParams - Object containing parent level filters
 * @returns Promise resolving to sorted array of {fsiId, fsiName} items
 */
export const queryEtmfStructure = async (type: string, queryParams?: EtmfQueryParams): Promise<EtmfStructureItem[]> => {
  // Build metadata filters based on hierarchy
  const filterParams = await assembleMetadataFilters(type, queryParams)

  // Query the API for items at this level
  const data = await instancedPropsSearch(type, filterParams)

  // Sort results by fsiName
  return sortByFsiName(data)
}

/**
 * Get filter parameters for master data query
 *
 * This method:
 * 1. Removes 'product' from query params (lowercase key)
 * 2. Assembles metadata filters for remaining hierarchy
 * 3. Filters out any params without vals (incomplete filters)
 *
 * Used in Update Study Dialog for:
 * - getCountryMDsForUpdate() - Building country metadata filters
 * - getSiteMDsForUpdate() - Building site metadata filters
 *
 * @param type - ETMF level type
 * @param queryParams - Object containing hierarchy parameters
 * @returns Promise resolving to array of metadata filters with vals only
 */
export const getFilterParamsForMdQuery = async (
  type?: string,
  queryParams?: EtmfQueryParams
): Promise<MetadataFilter[]> => {
  let params = queryParams || {}

  // Remove 'product' key (lowercase) from query params
  const { product, ...restParams } = params as any
  params = restParams

  // Assemble metadata filters
  const filterParams = await assembleMetadataFilters(type, params)

  // Filter out any params without vals (incomplete filters)
  return filterParams.filter((param) => !!param.vals)
}

/**
 * ETMF Service object - legacy compatibility export
 * Provides same interface as AngularJS etmfService
 */
export const etmfService = {
  sortByFsiName,
  assembleMetadataFilters,
  queryEtmfStructure,
  getFilterParamsForMdQuery
}

export default etmfService
